'use client';
import { useEffect, useLayoutEffect, useRef, useCallback, useMemo, memo, useState } from 'react';

/* ============================================================
   FlipDot — a flip-disc "web font" row.
   Each character is a 5x7 brick of dots that flip into place.
   Drop-in: <FlipDot text="ALOHA" />

   v4.2 adds reveal="wipe" — the Family Feud / airport-gate move:
   phase 1 fills the WHOLE display into a solid lit bar (left to
   right), a beat later phase 2 sweeps left to right again and the
   non-letter dots flip dark, so the word emerges out of the bar.
   Default reveal="scatter" is the original behavior, unchanged.
   ============================================================ */

const GLYPHS = {" ": [0, 0, 0, 0, 0, 0, 0], "!": [4, 4, 4, 4, 4, 0, 4], "\"": [9, 9, 18, 0, 0, 0, 0], "#": [10, 10, 31, 10, 31, 10, 10], "$": [4, 15, 20, 14, 5, 30, 4], "%": [25, 25, 2, 4, 8, 19, 19], "&": [4, 10, 10, 10, 21, 18, 13], "'": [4, 4, 8, 0, 0, 0, 0], "(": [2, 4, 8, 8, 8, 4, 2], ")": [8, 4, 2, 2, 2, 4, 8], "*": [4, 21, 14, 31, 14, 21, 4], "+": [0, 4, 4, 31, 4, 4, 0], ",": [0, 0, 0, 0, 4, 4, 8], "-": [0, 0, 0, 31, 0, 0, 0], ".": [0, 0, 0, 0, 0, 12, 12], "/": [1, 1, 2, 4, 8, 16, 16], "0": [14, 17, 19, 21, 25, 17, 14], "1": [4, 12, 4, 4, 4, 4, 14], "2": [14, 17, 1, 2, 4, 8, 31], "3": [14, 17, 1, 6, 1, 17, 14], "4": [2, 6, 10, 18, 31, 2, 2], "5": [31, 16, 30, 1, 1, 17, 14], "6": [6, 8, 16, 30, 17, 17, 14], "7": [31, 1, 2, 4, 8, 8, 8], "8": [14, 17, 17, 14, 17, 17, 14], "9": [14, 17, 17, 15, 1, 2, 12], ":": [0, 12, 12, 0, 12, 12, 0], ";": [0, 12, 12, 0, 12, 4, 8], "<": [2, 4, 8, 16, 8, 4, 2], "=": [0, 0, 31, 0, 31, 0, 0], ">": [8, 4, 2, 1, 2, 4, 8], "?": [14, 17, 1, 2, 4, 0, 4], "@": [14, 17, 23, 21, 23, 16, 15], "A": [4, 10, 17, 17, 31, 17, 17], "B": [30, 17, 17, 30, 17, 17, 30], "C": [14, 17, 16, 16, 16, 17, 14], "D": [30, 9, 9, 9, 9, 9, 30], "E": [31, 16, 16, 28, 16, 16, 31], "F": [31, 16, 16, 31, 16, 16, 16], "G": [14, 17, 16, 16, 19, 17, 15], "H": [17, 17, 17, 31, 17, 17, 17], "I": [14, 4, 4, 4, 4, 4, 14], "J": [31, 2, 2, 2, 2, 18, 12], "K": [17, 18, 20, 24, 20, 18, 17], "L": [16, 16, 16, 16, 16, 16, 31], "M": [17, 27, 21, 17, 17, 17, 17], "N": [17, 17, 25, 21, 19, 17, 17], "O": [14, 17, 17, 17, 17, 17, 14], "P": [30, 17, 17, 30, 16, 16, 16], "Q": [14, 17, 17, 17, 21, 18, 13], "R": [30, 17, 17, 30, 20, 18, 17], "S": [14, 17, 16, 14, 1, 17, 14], "T": [31, 4, 4, 4, 4, 4, 4], "U": [17, 17, 17, 17, 17, 17, 14], "V": [17, 17, 17, 17, 17, 10, 4], "W": [17, 17, 17, 21, 21, 27, 17], "X": [17, 17, 10, 4, 10, 17, 17], "Y": [17, 17, 10, 4, 4, 4, 4], "Z": [31, 1, 2, 4, 8, 16, 31], "[": [14, 8, 8, 8, 8, 8, 14], "\\": [16, 16, 8, 4, 2, 1, 1], "]": [14, 2, 2, 2, 2, 2, 14], "^": [4, 10, 17, 0, 0, 0, 0], "_": [0, 0, 0, 0, 0, 0, 31], "`": [4, 4, 2, 0, 0, 0, 0], "a": [0, 14, 1, 13, 19, 19, 13], "b": [16, 16, 16, 28, 18, 18, 28], "c": [0, 0, 0, 14, 16, 16, 14], "d": [1, 1, 1, 7, 9, 9, 7], "e": [0, 0, 14, 17, 31, 16, 15], "f": [6, 9, 8, 28, 8, 8, 8], "g": [14, 17, 19, 13, 1, 1, 14], "h": [16, 16, 16, 22, 25, 17, 17], "i": [0, 4, 0, 12, 4, 4, 14], "j": [2, 0, 6, 2, 2, 18, 12], "k": [16, 16, 18, 20, 24, 20, 18], "l": [12, 4, 4, 4, 4, 4, 4], "m": [0, 0, 10, 21, 21, 17, 17], "n": [0, 0, 22, 25, 17, 17, 17], "o": [0, 0, 14, 17, 17, 17, 14], "p": [0, 28, 18, 18, 28, 16, 16], "q": [0, 7, 9, 9, 7, 1, 1], "r": [0, 0, 22, 25, 16, 16, 16], "s": [0, 0, 15, 16, 14, 1, 30], "t": [8, 8, 28, 8, 8, 9, 6], "u": [0, 0, 17, 17, 17, 19, 13], "v": [0, 0, 17, 17, 17, 10, 4], "w": [0, 0, 17, 17, 21, 21, 10], "x": [0, 0, 17, 10, 4, 10, 17], "y": [0, 17, 17, 15, 1, 17, 14], "z": [0, 0, 31, 2, 4, 8, 31], "{": [6, 8, 8, 16, 8, 8, 6], "|": [4, 4, 4, 0, 4, 4, 4], "}": [12, 2, 2, 1, 2, 2, 12], "~": [8, 21, 2, 0, 0, 0, 0]};
const W = 5, H = 7;

// theme -> dot appearance (set imperatively so React re-renders never clobber a flip)
const THEMES = {
  amber: {
    on:  'radial-gradient(circle at 34% 30%, #fff66b, #ffe600 58%, #c9bc00)',
    off: 'radial-gradient(circle at 34% 30%, #6d6d6d, #4f4f4f 62%, #343434)',
    onShadow:  '0 0 6px rgba(255,224,0,.40), 0 1px 1px rgba(0,0,0,.35)',
    offShadow: 'inset 0 0 1px rgba(0,0,0,.45), 0 1px 1px rgba(0,0,0,.5)',
  },
  green: {
    on:  'radial-gradient(circle at 34% 30%, #c8ffb0, #5bff3a 58%, #2fae1f)',
    off: 'radial-gradient(circle at 34% 30%, #6d6d6d, #4f4f4f 62%, #343434)',
    onShadow:  '0 0 6px rgba(91,255,58,.40), 0 1px 1px rgba(0,0,0,.35)',
    offShadow: 'inset 0 0 1px rgba(0,0,0,.45), 0 1px 1px rgba(0,0,0,.5)',
  },
  white: {
    on:  'radial-gradient(circle at 34% 30%, #ffffff, #eef0ee 58%, #b9bdb7)',
    off: 'radial-gradient(circle at 34% 30%, #6d6d6d, #4f4f4f 62%, #343434)',
    onShadow:  '0 0 5px rgba(255,255,255,.35), 0 1px 1px rgba(0,0,0,.35)',
    offShadow: 'inset 0 0 1px rgba(0,0,0,.45), 0 1px 1px rgba(0,0,0,.5)',
  },
  // pure saturated yellow — used for PROJECT. status. Kept distinct from
  // the amber (#ffb300) used for the points split-flap so the two don't
  // read as the same color across columns.
  yellow: {
    on:  'radial-gradient(circle at 34% 30%, #fff7a8, #ffe600 55%, #d4c200)',
    off: 'radial-gradient(circle at 34% 30%, #6d6d6d, #4f4f4f 62%, #343434)',
    onShadow:  '0 0 6px rgba(255,230,0,.45), 0 1px 1px rgba(0,0,0,.35)',
    offShadow: 'inset 0 0 1px rgba(0,0,0,.45), 0 1px 1px rgba(0,0,0,.5)',
  },
};

const FLIP = [
  { transform: 'rotateX(0deg)' },
  { transform: 'rotateX(90deg)' },
  { transform: 'rotateX(0deg)' },
];

function glyphGrid(ch) {
  const g = GLYPHS[ch] || GLYPHS[' '];
  const out = new Array(W * H);
  for (let r = 0; r < H; r++) {
    const bits = g[r];
    for (let c = 0; c < W; c++) out[r * W + c] = !!(bits & (1 << (W - 1 - c)));
  }
  return out;
}
function paint(el, on, colors) {
  el.style.background = on ? colors.on : colors.off;
  el.style.boxShadow  = on ? colors.onShadow : colors.offShadow;
}
const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---- the dot grid: memoized so a text change never re-renders the dots ---- */
const DotGrid = memo(function DotGrid({ register, dotSize, gap }) {
  return (
    <span
      className="fd-brick"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${W}, ${dotSize}px)`,
        gap: `${gap}px`,
        perspective: `${Math.max(120, dotSize * 16)}px`,
      }}
    >
      {Array.from({ length: W * H }).map((_, k) => (
        <i
          key={k}
          ref={(el) => register(k, el)}
          aria-hidden="true"
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            display: 'block',
          }}
        />
      ))}
    </span>
  );
});

/* ---- one character cell ---- */
// Wipe props are plain numbers (not an object) so parent re-renders can't
// accidentally re-trigger the effect: wipeCol is this brick's first global
// column, wipeFillStep/wipeRevealStep are ms per column, wipeStart is the
// fill's start offset, wipeRevealStart is when the reveal sweep begins.
// wipeCol === null → classic scatter behavior, byte-for-byte the original.
function Brick({
  char, colors, dotSize, gap, delayBase, flipMs, dotStep,
  wipeCol = null, wipeStart = 0, wipeFillStep = 0, wipeRevealStart = 0, wipeRevealStep = 0,
}) {
  const dotsRef = useRef([]);
  const stateRef = useRef(null);     // logical on/off of each dot
  const colorsRef = useRef(null);
  const register = useCallback((k, el) => { dotsRef.current[k] = el; }, []);

  useLayoutEffect(() => {
    const els = dotsRef.current;
    if (!els.length) return;
    const target = glyphGrid(char);

    // first mount: paint everything off instantly (no flash)
    if (stateRef.current === null) {
      stateRef.current = new Array(W * H).fill(false);
      els.forEach((el) => el && paint(el, false, colors));
    } else if (colorsRef.current !== colors) {
      // theme changed: recolor current state without animating
      els.forEach((el, k) => el && paint(el, stateRef.current[k], colors));
    }
    colorsRef.current = colors;

    const reduced = prefersReduced();
    const timers = [];

    /* ── wipe mode: fill the bar, hold, sweep the word out of it ── */
    if (wipeCol !== null) {
      if (reduced) {
        els.forEach((el, k) => el && paint(el, target[k], colors));
        stateRef.current = target;
        return;
      }
      for (let k = 0; k < W * H; k++) {
        const el = els[k];
        if (!el) continue;
        const x = k % W;
        const gcol = wipeCol + x; // column across the WHOLE display
        const on = target[k];

        // phase 1: every dot lights up, left to right
        const d1 = wipeStart + gcol * wipeFillStep + Math.random() * wipeFillStep;
        if (!stateRef.current[k]) {
          el.animate(FLIP, { duration: flipMs, delay: d1, easing: 'ease-in-out' });
        }
        timers.push(setTimeout(() => paint(el, true, colors), d1 + flipMs / 2));

        // phase 2: non-letter dots flip dark, left to right — the word
        // is what's left standing.
        if (!on) {
          const d2 = wipeRevealStart + gcol * wipeRevealStep + Math.random() * wipeRevealStep;
          el.animate(FLIP, { duration: flipMs, delay: d2, easing: 'ease-in-out' });
          timers.push(setTimeout(() => paint(el, false, colors), d2 + flipMs / 2));
        }
      }
      stateRef.current = target;
      return () => timers.forEach(clearTimeout);
    }

    /* ── scatter mode: the original diff-and-flip ── */
    for (let k = 0; k < W * H; k++) {
      const on = target[k];
      if (on === stateRef.current[k]) continue;
      const el = els[k];
      if (!el) continue;
      if (reduced) { paint(el, on, colors); continue; }
      const x = k % W, y = (k / W) | 0;
      const delay = delayBase + (x + y) * dotStep + Math.random() * dotStep * 1.2;
      el.animate(FLIP, { duration: flipMs, delay, easing: 'ease-in-out' });
      timers.push(setTimeout(() => paint(el, on, colors), delay + flipMs / 2));
    }
    stateRef.current = target;
    return () => timers.forEach(clearTimeout);
  }, [char, colors, dotSize, gap, delayBase, flipMs, dotStep, wipeCol, wipeStart, wipeFillStep, wipeRevealStart, wipeRevealStep]);

  return <DotGrid register={register} dotSize={dotSize} gap={gap} />;
}

/* ---- the public component ---- */
export function FlipDot({
  text = '',
  dotSize = 9,      // px per dot
  gap = 2,          // px between dots
  brickGap,         // px between characters (defaults to one dot pitch)
  theme = 'amber',  // amber | green | white
  speed = 1,        // higher = faster
  bare = false,     // true = no board panel, transparent background
  reveal = 'scatter', // 'scatter' (original) | 'wipe' (fill bar → sweep word)
  revealDelay = 0,  // ms before a wipe begins (lets rows cascade)
  className = '',
  style = {},
}) {
  const chars = Array.from(String(text));
  const colors = useMemo(() => THEMES[theme] || THEMES.amber, [theme]);
  const flipMs = 300 / speed;
  const dotStep = 7 / speed;
  const perBrick = 85 / speed;
  const cGap = brickGap ?? dotSize + gap;

  // Wipe timing, all scaled by speed. Fill sweeps fast, the hold gives
  // the solid bar a beat to register, the reveal sweeps a touch slower
  // so the word visibly "wipes" in. For 8 chars (40 cols) at speed 1:
  // ~440ms fill + 300ms hold + ~800ms reveal ≈ 1.5s total.
  const totalCols = chars.length * W;
  const wipeFillStep = 11 / speed;
  const wipeRevealStep = 20 / speed;
  const wipeRevealStart = revealDelay + totalCols * wipeFillStep + 300 / speed;

  const board = bare
    ? { background: 'transparent', padding: 0, boxShadow: 'none' }
    : {
        background: '#080809',
        padding: `${dotSize + gap}px ${dotSize + gap}px`,
        borderRadius: `${Math.max(6, dotSize)}px`,
        boxShadow:
          '0 0 0 1px #000 inset, 0 8px 22px rgba(0,0,0,.45) inset, 0 6px 18px -8px rgba(0,0,0,.7)',
      };

  return (
    <span
      className={`flip-dot ${className}`}
      aria-label={text}
      style={{
        display: 'inline-flex',
        gap: `${cGap}px`,
        lineHeight: 0,
        verticalAlign: 'middle',
        ...board,
        ...style,
      }}
    >
      {chars.map((ch, i) => (
        <Brick
          key={i}
          char={ch}
          colors={colors}
          dotSize={dotSize}
          gap={gap}
          delayBase={i * perBrick}
          flipMs={flipMs}
          dotStep={dotStep}
          wipeCol={reveal === 'wipe' ? i * W : null}
          wipeStart={revealDelay}
          wipeFillStep={wipeFillStep}
          wipeRevealStart={wipeRevealStart}
          wipeRevealStep={wipeRevealStep}
        />
      ))}
    </span>
  );
}
export default FlipDot;
